import type {ReactNode} from 'react'
import {useRef, useState, useEffect, useCallback, useMemo} from 'react'
import type {CustomAgentSessionGroup} from '../../utils/group-agent-logs'
import {formatAgentTitle, isCustomAgentSessionGroup} from '../../utils/group-agent-logs'
import {isCompletionChunk} from '../../types/session'
import {TOOL_NAMES} from '../../constants/tool-names'
import {NewToolHeader} from './tool/NewToolHeader'
import styles from './CustomAgentSessionGroup.module.css'
import toolGroupStyles from './tool-grouping/ToolGroupRenderer.module.css'
import {DependabotIcon} from '@primer/octicons-react'
import {ExpandableMarkdown} from '../expandable-markdown/ExpandableMarkdown'

// TODO: Unify with TOOL_CALL_TITLES in Tool.tsx — extract both into a shared
// constants/tool-display-names.ts so tool display names have a single source of truth.
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  [TOOL_NAMES.BASH]: 'Run Bash command',
  [TOOL_NAMES.VIEW]: 'View file',
  [TOOL_NAMES.EDIT]: 'Edit file',
  [TOOL_NAMES.CREATE]: 'Create file',
  [TOOL_NAMES.GREP]: 'Search',
  [TOOL_NAMES.RG]: 'Search',
  [TOOL_NAMES.GLOB]: 'Find files',
  [TOOL_NAMES.STR_REPLACE_EDITOR]: 'Edit file',
  [TOOL_NAMES.STR_REPLACE]: 'Edit file',
  [TOOL_NAMES.APPLY_PATCH]: 'Apply patch',
  [TOOL_NAMES.WEB_SEARCH]: 'Web search',
  [TOOL_NAMES.WEB_FETCH]: 'Fetch URL',
  [TOOL_NAMES.THINK]: 'Thinking',
  [TOOL_NAMES.COMMIT_CHANGES]: 'Commit changes',
}

interface CustomAgentSessionGroupProps {
  customAgentSession: CustomAgentSessionGroup
  children: ReactNode
}

/**
 * Extracts a human-readable title for the last tool call in the group's logs.
 * Prioritises the description field (set by the agent for bash/shell tools),
 * then falls back to path for file tools, then just the display name.
 */
function getLastToolCallTitle(group: CustomAgentSessionGroup): string | null {
  for (let i = group.logs.length - 1; i >= 0; i--) {
    const log = group.logs[i]
    if (!log || isCustomAgentSessionGroup(log) || !isCompletionChunk(log)) continue
    for (const choice of log.choices) {
      const toolCalls = choice.delta?.tool_calls ?? []
      for (const tc of toolCalls) {
        const name = tc?.function?.name ?? tc?.custom?.name
        if (!name) continue
        const displayName = TOOL_DISPLAY_NAMES[name] ?? name
        const args = parseToolCallArgs(tc?.function?.arguments)
        if (!args) return displayName

        // Prefer agent-provided description (e.g., "Read main files in the repository")
        if (args.description) return args.description
        // File path for view/edit/create tools
        if (args.path) return `${displayName}: ${args.path}`
        // Search pattern for grep/glob
        if (args.pattern) return `${displayName}: ${args.pattern}`
        // Command for bash (truncated)
        if (args.command) return `${displayName}: ${args.command.slice(0, 60)}`
        return displayName
      }
    }
  }
  return null
}

function parseToolCallArgs(raw: string | undefined): Record<string, string> | null {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Renders a nested subagent session with visual indication of the grouping.
 *
 * When collapsed, shows the last tool call name inline after the title as muted text.
 * When expanded, shows full content with prompt bubble at the top.
 */
export function CustomAgentSessionGroup({customAgentSession, children}: CustomAgentSessionGroupProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const agentInstanceRef = useRef({})

  const title = formatAgentTitle(customAgentSession)
  const isPending = customAgentSession.isPending
  const prompt = customAgentSession.prompt

  const lastToolTitle = useMemo(() => getLastToolCallTitle(customAgentSession), [customAgentSession])
  const showLastTool = !isDetailsOpen && isPending && lastToolTitle

  // Listen for global expand/collapse events
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{expand: boolean; excludeInstance: object}>
      if (customEvent.detail.excludeInstance !== agentInstanceRef.current) {
        setIsDetailsOpen(customEvent.detail.expand)
      }
    }

    document.addEventListener('mission-control-tool-global-toggle', handler)
    return () => {
      document.removeEventListener('mission-control-tool-global-toggle', handler)
    }
  }, [])

  const handleModifierClick = useCallback((expand: boolean) => {
    setIsDetailsOpen(expand)

    const event = new CustomEvent('mission-control-tool-global-toggle', {
      detail: {expand, excludeInstance: agentInstanceRef.current},
    })
    document.dispatchEvent(event)
  }, [])

  const handleHeaderToggle = useCallback(() => {
    setIsDetailsOpen(prev => !prev)
  }, [])

  return (
    <div className={styles.detailsContainer}>
      <NewToolHeader
        title={title}
        isCollapsed={!isDetailsOpen}
        isPending={isPending}
        onModifierClick={handleModifierClick}
        onToggle={handleHeaderToggle}
        leadingVisual={<DependabotIcon />}
        statusText={showLastTool ? lastToolTitle : undefined}
      />
      {isDetailsOpen && (
        <div className={styles.toolContentWrapper}>
          <div className={toolGroupStyles.connectorLine} />
          <div className={styles.toolContent}>
            {prompt && (
              <div className={styles.promptBubble}>
                <ExpandableMarkdown markdown={prompt} className={styles.promptText} />
              </div>
            )}
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
